import React, { useState, useRef, useEffect } from "react";
import "./Terminal.css";
import CommandInput from "./CommandInput";
import CommandHistory from "./CommandHistory";
import Suggestions from "./Suggestions";
import AVAILABLE_COMMANDS from "../../config/commands";
import resumeData from "../../data/resumeData";

const Terminal = ({ initialCommand }) => {
  const [commandHistory, setCommandHistory] = useState([initialCommand]);
  const [currentCommand, setCurrentCommand] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [availableCommands, setAvailableCommands] =
    useState(AVAILABLE_COMMANDS);
  const inputRef = useRef(null);

  // Limit the number of commands in history to keep UI clean
  const MAX_COMMAND_HISTORY = 10;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Effect to limit command history length
  useEffect(() => {
    if (commandHistory.length > MAX_COMMAND_HISTORY) {
      // Keep the first entry (initial console.log) and most recent commands
      setCommandHistory((prev) => [
        prev[0],
        ...prev.slice(prev.length - MAX_COMMAND_HISTORY + 1),
      ]);
    }
  }, [commandHistory.length]);

  const handleCommandChange = (value) => {
    setCurrentCommand(value);

    // Filter suggestions from available commands
    const filteredSuggestions = availableCommands.filter(
      (cmd) =>
        cmd.name.toLowerCase().includes(value.toLowerCase()) ||
        cmd.command.toLowerCase().includes(value.toLowerCase())
    );

    setSuggestions(filteredSuggestions);
    setActiveSuggestionIndex(-1);
  };

  // Resume section handlers
  const handleResumeCommand = () => {
    return (
      <div className="resume-download">
        <p>Download my resume:</p>
        <a
          href={resumeData.resumePdfPath}
          download="Nima_Najaflou_Resume.pdf"
          className="download-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here to download PDF
        </a>
        <p className="note">
          (Or view my resume sections by typing: 'experience', 'education',
          'skills', etc.)
        </p>
      </div>
    );
  };

  const handleContactCommand = () => {
    const { email, phone, location, linkedin } = resumeData.contact;
    return (
      <div className="contact-info">
        <p>
          <span className="highlight">Email:</span>{" "}
          <a href={`mailto:${email}`}>{email}</a>
        </p>
        <p>
          <span className="highlight">Phone:</span>{" "}
          <a href={`tel:${phone}`}>{phone}</a>
        </p>
        <p>
          <span className="highlight">Location:</span> {location}
        </p>
        <p>
          <span className="highlight">LinkedIn:</span>{" "}
          <a href={linkedin} target="_blank" rel="noopener noreferrer">
            {linkedin}
          </a>
        </p>
      </div>
    );
  };

  const handleProjectsCommand = () => {
    return (
      <div className="projects-list">
        <p>My Projects:</p>
        <ul>
          {resumeData.projects.map((project, index) => (
            <li key={index}>
              <span className="highlight">{project.name}</span>{" "}
              <a href={project.url} target="_blank" rel="noopener noreferrer">
                {project.url}
              </a>{" "}
              [{project.tech}]
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleSkillsCommand = () => {
    const {
      frontend,
      backend,
      databases,
      tools,
      hardware,
      networks,
      security,
    } = resumeData.skills;
    return (
      <div className="skills-list">
        <p className="section-title">Technical Skills:</p>
        <p>
          <span className="highlight">Front-End Development:</span> {frontend}
        </p>
        <p>
          <span className="highlight">Back-End Development:</span> {backend}
        </p>
        <p>
          <span className="highlight">Databases:</span> {databases}
        </p>
        <p>
          <span className="highlight">Tools & Technologies:</span> {tools}
        </p>
        <p>
          <span className="highlight">Computer Hardware:</span> {hardware}
        </p>
        <p>
          <span className="highlight">Networking:</span> {networks}
        </p>
        <p>
          <span className="highlight">Security:</span> {security}
        </p>
      </div>
    );
  };

  const handleExperienceCommand = () => {
    return (
      <div className="experience-list">
        <p className="section-title">Work Experience:</p>
        {resumeData.experience.map((job, index) => (
          <div key={index} className="experience-item">
            <p>
              <span className="highlight">
                {job.period} | {job.title}
              </span>
            </p>
            <p>
              {job.company}, {job.location}
            </p>
            <ul>
              {job.responsibilities.map((responsibility, idx) => (
                <li key={idx}>{responsibility}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const handleEducationCommand = () => {
    return (
      <div className="education-list">
        <p className="section-title">Education:</p>
        {resumeData.education.map((edu, index) => (
          <div key={index} className="education-item">
            <p>
              <span className="highlight">
                {edu.year} | {edu.degree}
              </span>
            </p>
            <p>
              {edu.institution}, {edu.location}
            </p>
          </div>
        ))}

        {resumeData.certifications.length > 0 && (
          <>
            <p className="section-title">Certifications:</p>
            {resumeData.certifications.map((cert, index) => (
              <div key={index} className="certification-item">
                <p>
                  <span className="highlight">
                    {cert.name} ({cert.date})
                  </span>
                </p>
                <p>{cert.description}</p>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const handleCommandSubmit = (e) => {
    e.preventDefault();

    if (!currentCommand.trim()) return;

    // Check if command exists in available commands
    const matchedCommandIndex = availableCommands.findIndex(
      (cmd) => cmd.command.toLowerCase() === currentCommand.toLowerCase()
    );

    const matchedCommand =
      matchedCommandIndex !== -1
        ? availableCommands[matchedCommandIndex]
        : null;

    if (currentCommand.toLowerCase() === "clear") {
      // Special handling for clear command
      // Keep only the first history item (the initial console.log)
      const initialCommand = commandHistory[0];
      setCommandHistory([initialCommand]);

      // Reset all available commands when clearing the terminal
      setAvailableCommands(AVAILABLE_COMMANDS);
    } else if (currentCommand.toLowerCase() === "help") {
      // Special handling for help command
      setCommandHistory((prev) => [
        ...prev,
        {
          command: `> ${currentCommand}`,
          response: (
            <div>
              <div style={{ marginBottom: "10px" }}>Available commands:</div>
              {availableCommands.map((cmd, index) => (
                <div
                  key={index}
                  style={{ marginLeft: "10px", marginBottom: "5px" }}
                >
                  <strong>{cmd.command}</strong> - {cmd.description}
                </div>
              ))}
            </div>
          ),
        },
      ]);
    } else if (matchedCommand) {
      // Handle resume-specific commands
      let commandResponse;

      switch (currentCommand.toLowerCase()) {
        case "resume":
          commandResponse = handleResumeCommand();
          break;
        case "contact":
          commandResponse = handleContactCommand();
          break;
        case "projects":
          commandResponse = handleProjectsCommand();
          break;
        case "skills":
          commandResponse = handleSkillsCommand();
          break;
        case "experience":
          commandResponse = handleExperienceCommand();
          break;
        case "education":
          commandResponse = handleEducationCommand();
          break;
        default:
          commandResponse = (
            <div>Executing {matchedCommand.name} command...</div>
          );
      }

      // Add command to history with response
      setCommandHistory((prev) => [
        ...prev,
        {
          command: `> ${currentCommand}`,
          response: commandResponse,
        },
      ]);

      // Don't remove resume section commands from available commands
      const permanentCommands = [
        "clear",
        "help",
        "resume",
        "contact",
        "projects",
        "skills",
        "experience",
        "education",
      ];
      if (!permanentCommands.includes(matchedCommand.command.toLowerCase())) {
        setAvailableCommands((prev) =>
          prev.filter((_, index) => index !== matchedCommandIndex)
        );
      }
    } else {
      // Command not found
      setCommandHistory((prev) => [
        ...prev,
        {
          command: `> ${currentCommand}`,
          response: <div>command not found: {currentCommand}</div>,
        },
      ]);
    }

    // Reset input
    setCurrentCommand("");
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion) => {
    setCurrentCommand(suggestion.command);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    inputRef.current?.focus();

    // Small delay to allow state updates before potentially submitting
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleKeyDown = (e) => {
    // Handle arrow down
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveSuggestionIndex((prev) => {
          const newIndex =
            prev === -1 ? 0 : Math.min(prev + 1, suggestions.length - 1);
          return newIndex;
        });
      }
    }
    // Handle arrow up
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveSuggestionIndex((prev) => {
          const newIndex = Math.max(prev - 1, 0);
          return newIndex;
        });
      }
    }
    // Handle enter
    else if (e.key === "Enter") {
      // If a suggestion is actively selected
      if (activeSuggestionIndex !== -1 && suggestions.length > 0) {
        e.preventDefault();
        const selectedSuggestion = suggestions[activeSuggestionIndex];
        setCurrentCommand(selectedSuggestion.command);
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
      }
      // Otherwise, treat as normal form submission
    }
    // Handle tab
    else if (e.key === "Tab" && suggestions.length > 0) {
      e.preventDefault();
      // Auto-complete with the first suggestion
      setCurrentCommand(suggestions[0].command);
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
    // Handle escape
    else if (e.key === "Escape") {
      e.preventDefault();
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  return (
    <div className="terminal">
      <CommandHistory commandHistory={commandHistory} />

      <div style={{ position: "relative", width: "100%" }}>
        <CommandInput
          value={currentCommand}
          onChange={handleCommandChange}
          onSubmit={handleCommandSubmit}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
        />

        {suggestions.length > 0 && (
          <Suggestions
            suggestions={suggestions}
            activeIndex={activeSuggestionIndex}
            onClick={handleSuggestionClick}
            onMouseEnter={setActiveSuggestionIndex}
          />
        )}

        {currentCommand && suggestions.length === 0 && (
          <div className="command-suggestions">
            <div className="suggestion-item">No matching prompts</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Terminal;
